import React, { useEffect, useState } from "react";
import {
  Box, Typography, Container, Modal, IconButton, Snackbar, Alert, Chip, Divider,
} from "@mui/material";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import moment from "moment";
import axios from "axios";
import config from "../../config";

const featuredUrls = [
  "https://www.youtube.com/embed/61dtNjwm8Zc?si=EDgYbNNOK9kyXYYT",
  "https://www.youtube.com/embed/neMSYpJYP54?si=x5wRGz9Dp97KCtl0",
  "https://www.youtube.com/embed/aUbE4gbRH1c?si=6kd0JnS6zPf0gec3",
];

/* ── Section heading ─────────────────────────────────── */
const SectionHeading = ({ icon, label, accent, count }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {icon}
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: { xs: "20px", md: "26px" } }}>
          {label}{" "}
          <Box component="span" sx={{ color: "#FFC001" }}>{accent}</Box>
        </Typography>
      </Box>
      {count != null && (
        <Typography sx={{ color: "#555", fontSize: "13px", mt: 0.3 }}>{count} item{count !== 1 ? "s" : ""}</Typography>
      )}
    </Box>
  </Box>
);

/* ── Video embed card ─────────────────────────────────── */
const VideoCard = ({ src, title, publishedAt, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, ease: "easeOut", delay }}
  >
    <Box
      sx={{
        backgroundColor: "#1a1a1a",
        border: "1px solid #2a2a2a",
        borderRadius: "12px",
        overflow: "hidden",
        transition: "all 0.25s ease",
        "&:hover": { border: "1px solid #FFC001", boxShadow: "0 8px 24px rgba(255,192,1,0.08)", transform: "translateY(-3px)" },
      }}
    >
      <Box sx={{ position: "relative", paddingTop: "56.25%" }}>
        <iframe
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
          src={src}
          title={title || "YouTube video"}
          allow="accelerometer; fullscreen; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </Box>
      {(title || publishedAt) && (
        <Box sx={{ px: 1.5, py: 1.2 }}>
          {title && (
            <Typography noWrap sx={{ color: "#fff", fontWeight: 600, fontSize: "13px", mb: 0.3 }}>{title}</Typography>
          )}
          {publishedAt && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 12, color: "#555" }} />
              <Typography sx={{ color: "#555", fontSize: "12px" }}>{moment(publishedAt).fromNow()}</Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  </motion.div>
);

/* ── Image card ───────────────────────────────────────── */
const ImageCard = ({ item, onClick, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, ease: "easeOut", delay }}
  >
    <Box
      onClick={onClick}
      sx={{
        backgroundColor: "#1a1a1a",
        border: "1px solid #2a2a2a",
        borderRadius: "12px",
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.25s ease",
        "&:hover": {
          border: "1px solid #FFC001",
          boxShadow: "0 8px 24px rgba(255,192,1,0.08)",
          transform: "translateY(-3px)",
          "& .img-overlay": { opacity: 1 },
        },
      }}
    >
      <Box sx={{ position: "relative", paddingTop: "75%", overflow: "hidden" }}>
        <img
          src={item.image}
          alt={item.title}
          loading="lazy"
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.3s ease" }}
        />
        <Box
          className="img-overlay"
          sx={{
            position: "absolute", inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            opacity: 0, transition: "opacity 0.25s ease",
          }}
        >
          <PlayCircleOutlineIcon sx={{ color: "#FFC001", fontSize: 44 }} />
        </Box>
      </Box>
      {item.title && (
        <Box sx={{ px: 1.5, py: 1.2 }}>
          <Typography noWrap sx={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>{item.title}</Typography>
        </Box>
      )}
    </Box>
  </motion.div>
);

/* ── Grid wrapper ─────────────────────────────────────── */
const CardGrid = ({ children }) => (
  <Box sx={{
    display: "grid", gap: 2,
    gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" },
  }}>
    {children}
  </Box>
);

/* ══════════════════════════════════════════════════════ */
const Media = () => {
  const [playlistVideos, setPlaylistVideos] = useState([]);
  const [animalsMedia, setAnimalsMedia] = useState([]);
  const [showAllAnimals, setShowAllAnimals] = useState(false);
  const [showAllVideos, setShowAllVideos] = useState(false);
  const [modalSrc, setModalSrc] = useState(null);   // iframe src string
  const [modalImg, setModalImg] = useState(null);   // image object
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  useEffect(() => {
    const fetchPlaylist = async () => {
      try {
        const res = await axios.get(
          `https://www.googleapis.com/youtube/v3/playlistItems?playlistId=${process.env.REACT_APP_YT_PLAYLIST_ID}&key=${process.env.REACT_APP_YOUTUBE_API_KEY}&part=id,snippet,contentDetails,status`
        );
        if (res.status === 200) {
          const sorted = res.data.items.sort(
            (a, b) => new Date(b.contentDetails.videoPublishedAt) - new Date(a.contentDetails.videoPublishedAt)
          );
          setPlaylistVideos(sorted);
        }
      } catch {
        setSnackbar({ open: true, message: "Unable to load videos right now." });
      }
    };

    const fetchAnimals = async () => {
      try {
        const res = await axios.get(`${config.SERVER_URL}/api/v1/animals_media`);
        if (res.status === 200) setAnimalsMedia(res.data.data);
      } catch {
        setSnackbar({ open: true, message: "Unable to load media right now." });
      }
    };

    fetchPlaylist();
    fetchAnimals();
  }, []);

  const closeModal = () => { setModalSrc(null); setModalImg(null); };

  const visibleAnimals = showAllAnimals ? animalsMedia : animalsMedia.slice(0, 8);
  const visibleVideos = showAllVideos ? playlistVideos : playlistVideos.slice(0, 8);

  return (
    <>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} variant="filled" severity="error" sx={{ width: "100%" }}>{snackbar.message}</Alert>
      </Snackbar>

      {/* ── Lightbox modal ── */}
      <Modal open={!!(modalSrc || modalImg)} onClose={closeModal}>
        <Box
          sx={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95vw", md: "75vw", lg: "65vw" },
            backgroundColor: "#111",
            border: "1px solid #333",
            borderRadius: "16px",
            overflow: "hidden",
            outline: "none",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
            <IconButton onClick={closeModal} sx={{ color: "#777", "&:hover": { color: "#FFC001" } }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ px: 2, pb: 2 }}>
            {modalSrc && (
              <Box sx={{ position: "relative", paddingTop: "56.25%" }}>
                <iframe
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
                  src={modalSrc}
                  title="Video"
                  allow="accelerometer; fullscreen; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </Box>
            )}
            {modalImg && (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <img src={modalImg.image} alt={modalImg.title} style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: "8px", objectFit: "contain" }} />
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      <Box sx={{ minHeight: "100vh", backgroundColor: "#111" }}>
        {/* ── Hero header ── */}
        <Box sx={{ position: "relative", background: "linear-gradient(to bottom, #1a1a1a 0%, #111 100%)", borderBottom: "1px solid #222", py: { xs: 5, md: 7 }, overflow: "hidden" }}>
          <Box sx={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 0%, rgba(255,192,1,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />
          <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
            {/* Breadcrumb */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
              <Typography component={Link} to="/" sx={{ color: "#777", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}>Home</Typography>
              <Typography sx={{ color: "#555", fontSize: "13px" }}>/</Typography>
              <Typography sx={{ color: "#FFC001", fontSize: "13px", fontWeight: 600 }}>Media</Typography>
            </Box>
            {/* Title */}
            <Box sx={{ textAlign: "center" }}>
              <Chip
                label="MOORE EXOTICS"
                size="small"
                sx={{ backgroundColor: "rgba(255,192,1,0.12)", color: "#FFC001", fontWeight: 700, fontSize: "11px", letterSpacing: "0.08em", border: "1px solid rgba(255,192,1,0.3)", mb: 2 }}
              />
              <Typography sx={{ fontWeight: 800, fontSize: { xs: "28px", sm: "36px", md: "48px" }, color: "#fff", lineHeight: 1.1, mb: 1 }}>
                Our{" "}
                <Box component="span" sx={{ color: "#FFC001" }}>Media</Box>
              </Typography>
              <Typography sx={{ color: "#777", fontSize: { xs: "13px", md: "15px" } }}>
                Videos, photos, and content from the Moore Exotics community
              </Typography>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>

          {/* ── Featured Videos ── */}
          <SectionHeading
            icon={<OndemandVideoIcon sx={{ color: "#FFC001", fontSize: 22 }} />}
            label="Featured"
            accent="Videos"
            count={featuredUrls.length}
          />
          <CardGrid>
            {featuredUrls.map((src, i) => (
              <VideoCard key={src} src={src} delay={i * 0.05} />
            ))}
          </CardGrid>

          {/* ── Recent Uploads (from YouTube playlist) ── */}
          {playlistVideos.length > 0 && (
            <>
              <Divider sx={{ borderColor: "#222", my: 5 }} />
              <SectionHeading
                icon={<OndemandVideoIcon sx={{ color: "#FFC001", fontSize: 22 }} />}
                label="Recent"
                accent="Uploads"
                count={playlistVideos.length}
              />
              <CardGrid>
                {visibleVideos.map((item, i) => (
                  <VideoCard
                    key={item.id}
                    src={`https://www.youtube.com/embed/${item.snippet.resourceId.videoId}`}
                    title={item.snippet.title}
                    publishedAt={item.contentDetails.videoPublishedAt}
                    delay={(i % 4) * 0.05}
                  />
                ))}
              </CardGrid>
              {playlistVideos.length > 8 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                  <Box
                    onClick={() => setShowAllVideos((v) => !v)}
                    sx={{ cursor: "pointer", border: "1px solid #333", color: "#ccc", fontWeight: 600, fontSize: "14px", px: 4, py: 1.2, borderRadius: "24px", "&:hover": { borderColor: "#FFC001", color: "#FFC001" } }}
                  >
                    {showAllVideos ? "Show Less" : `Show All ${playlistVideos.length} Videos`}
                  </Box>
                </Box>
              )}
            </>
          )}

          {/* ── Animals Media ── */}
          {animalsMedia.length > 0 && (
            <>
              <Divider sx={{ borderColor: "#222", my: 5 }} />
              <SectionHeading
                icon={<PhotoLibraryIcon sx={{ color: "#FFC001", fontSize: 22 }} />}
                label="Animals"
                accent="Gallery"
                count={animalsMedia.length}
              />
              <CardGrid>
                {visibleAnimals.map((item, i) => (
                  <ImageCard
                    key={item.id || i}
                    item={item}
                    onClick={() => setModalImg(item)}
                    delay={(i % 4) * 0.05}
                  />
                ))}
              </CardGrid>
              {animalsMedia.length > 8 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                  <Box
                    onClick={() => setShowAllAnimals((v) => !v)}
                    sx={{ cursor: "pointer", border: "1px solid #333", color: "#ccc", fontWeight: 600, fontSize: "14px", px: 4, py: 1.2, borderRadius: "24px", "&:hover": { borderColor: "#FFC001", color: "#FFC001" } }}
                  >
                    {showAllAnimals ? "Show Less" : `Show All ${animalsMedia.length} Photos`}
                  </Box>
                </Box>
              )}
            </>
          )}

        </Container>
      </Box>
    </>
  );
};

export default Media;
